import { Suspense, lazy, useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { BoardView } from './views/BoardView.js';
import { ImListView } from './views/ImListView.js';
import { ImDetailView } from './views/ImDetailView.js';
import { LeadsView } from './views/LeadsView.js';
import { signOut, type AuthUser } from './components/RequireAuth.js';
import { SessionProvider, useSession } from './components/Session.js';

// Code-split the analytics view: it pulls in recharts (~375 kB), which the
// default board view shouldn't pay for. Only loaded when /analytics is visited.
const AnalyticsView = lazy(() =>
  import('./views/AnalyticsView.js').then((m) => ({ default: m.AnalyticsView })),
);

function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `relative rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-white/[0.07] text-ink-100'
            : 'text-ink-400 hover:bg-white/[0.04] hover:text-ink-100'
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function Logo() {
  return (
    <span className="flex shrink-0 items-center gap-2.5">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-accent-400 to-accent-500 text-sm font-bold text-white shadow-[0_4px_14px_-4px_rgb(108_116_245/0.7)]">
        W
      </span>
      <span className="hidden text-[15px] font-semibold tracking-tight sm:inline">
        Wakeflow <span className="font-normal text-ink-400">Dealflow</span>
      </span>
    </span>
  );
}

/** Switch the whole app to a teammate's view, or back, then start afresh. */
async function viewAs(email: string | null): Promise<void> {
  await fetch('/api/auth/view-as', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  window.location.assign('/board');
}

/**
 * The admin's "View as" menu: the app as a teammate sees it, with their deals,
 * buttons and tabs. The server treats every request as theirs, read-only.
 */
function ViewAsControl({ user }: { user: AuthUser }) {
  const { team } = useSession();
  const [busy, setBusy] = useState(false);
  const admin = (user.viewed_by?.email ?? user.email).toLowerCase();
  const others = team.filter((m) => m.email !== admin);
  if (others.length === 0) return null;
  return (
    <label className="hidden items-center gap-2 text-ink-400 md:flex">
      View as
      <select
        value={user.viewed_by ? user.email.toLowerCase() : ''}
        disabled={busy}
        onChange={(e) => {
          setBusy(true);
          void viewAs(e.target.value || null);
        }}
        className="rounded-lg border border-white/10 bg-ink-800 px-2 py-1.5 text-xs text-ink-100 outline-none hover:border-white/20 focus:border-accent-400"
      >
        <option value="">Myself</option>
        {others.map((m) => (
          <option key={m.email} value={m.email}>
            {m.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function ViewingAsBanner({ user }: { user: AuthUser }) {
  const [busy, setBusy] = useState(false);
  if (!user.viewed_by) return null;
  return (
    <div className="border-b border-warn-500/20 bg-warn-500/10">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs text-warn-400 sm:px-6">
        <span>
          You’re seeing Dealflow the way{' '}
          <span className="font-semibold">{user.name || user.email}</span> sees it. Read-only:
          nothing you click here changes anything.
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void viewAs(null);
          }}
          className="btn bg-warn-500/20 text-warn-400 hover:bg-warn-500/30"
        >
          Back to my view
        </button>
      </div>
    </div>
  );
}

export default function App({ user }: { user: AuthUser }) {
  const [loggingOut, setLoggingOut] = useState(false);
  const isAdmin = user.role === 'admin';

  const handleLogout = () => {
    setLoggingOut(true);
    void signOut();
  };

  return (
    <SessionProvider user={user}>
    <div className="min-h-full flex flex-col">
      <header className="sticky top-0 z-10 border-b border-white/[0.06] bg-ink-900/75 backdrop-blur-xl">
        <div className="flex h-14 w-full items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3 sm:gap-8">
            <Logo />
            <nav className="flex items-center gap-0.5">
              <NavItem to="/board" label="Board" />
              <NavItem to="/ims" label="Deals" />
              {/* Leads are the admin's inbox of things to chase; the rest of
                  the team only works deals. The API refuses them too. */}
              {isAdmin ? <NavItem to="/leads" label="Leads" /> : null}
              <NavItem to="/analytics" label="Analytics" />
            </nav>
          </div>
          <div className="flex shrink-0 items-center gap-3 text-xs">
            {isAdmin || user.viewed_by ? <ViewAsControl user={user} /> : null}
            <span className="hidden items-center gap-2.5 lg:flex" title={user.email}>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-ink-100">
                {(user.name || user.email).charAt(0).toUpperCase()}
              </span>
              <span className="flex flex-col leading-tight">
                <span className="max-w-[200px] truncate font-medium text-ink-100">
                  {user.name || user.email}
                </span>
                <span className="text-[11px] text-ink-400">
                  {isAdmin ? 'Admin' : 'Team member'}
                </span>
              </span>
            </span>
            <button onClick={handleLogout} disabled={loggingOut} className="btn-ghost">
              {loggingOut ? 'Signing out…' : 'Log out'}
            </button>
          </div>
        </div>
        <ViewingAsBanner user={user} />
      </header>

      <main className="w-full flex-1 px-4 py-6 sm:px-6">
        <Suspense
          fallback={<div className="card p-8 text-center text-ink-300 text-sm">Loading…</div>}
        >
          <Routes>
            {/* The board is the default view: the pipeline is the thing the team
                works from, and moving a card there is what instructs the agent. */}
            <Route path="/" element={<Navigate to="/board" replace />} />
            <Route path="/board" element={<BoardView />} />
            <Route path="/ims" element={<ImListView />} />
            <Route path="/ims/:id" element={<ImDetailView />} />
            <Route
              path="/leads"
              element={isAdmin ? <LeadsView /> : <Navigate to="/board" replace />}
            />
            <Route path="/analytics" element={<AnalyticsView />} />
            <Route path="*" element={<Navigate to="/board" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
    </SessionProvider>
  );
}
